import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Statistic, Segmented, Space } from "antd";
import {
  ShoppingOutlined,
  StopOutlined,
  AppstoreOutlined,
  ShoppingCartOutlined,
  CarOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AdminProductService from "../../../Modules/Services/admin.products.service.ts";
import AdminCategoryService from "../../../Modules/Services/admin.categories.service";
import AdminOrdersService from "../../../Modules/Services/admin.orders.service";

const formatRub = (value) =>
  new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDayLabel = (value) => {
  const s = String(value || "");
  const parts = s.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}`;
  return s;
};

const OrdersChart = ({ series }) => {
  const data = useMemo(() => {
    const arr = Array.isArray(series) ? series : [];
    return arr.map((p) => ({
      day: p.day,
      dayLabel: formatDayLabel(p.day),
      orders: Number(p.orders || 0),
      totalSum: Number(p.totalSum || 0),
    }));
  }, [series]);

  // Recharts uses inline SVG: CSS vars may not resolve in attributes, so set via inline color
  const accent = "#c68e72";

  return (
    <div style={{ width: "100%", height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="ordersFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
              <stop offset="100%" stopColor={accent} stopOpacity={0.03} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(0,0,0,0.08)" strokeDasharray="4 4" />
          <XAxis
            dataKey="dayLabel"
            tick={{ fill: "rgba(0,0,0,0.65)", fontSize: 12 }}
            axisLine={{ stroke: "rgba(0,0,0,0.12)" }}
            tickLine={{ stroke: "rgba(0,0,0,0.12)" }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: "rgba(0,0,0,0.65)", fontSize: 12 }}
            axisLine={{ stroke: "rgba(0,0,0,0.12)" }}
            tickLine={{ stroke: "rgba(0,0,0,0.12)" }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            }}
            labelFormatter={(label, payload) => {
              const p = payload?.[0]?.payload;
              return p?.day ? `Дата: ${p.day}` : label;
            }}
            formatter={(value, name, payload) => {
              if (name === "orders") return [value, "Заказы"];
              return [value, name];
            }}
          />
          <Area
            type="monotone"
            dataKey="orders"
            stroke={accent}
            strokeWidth={3}
            fill="url(#ordersFill)"
            dot={false}
            activeDot={{ r: 5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    stopListProducts: 0,
    totalCategories: 0,
  });
  const [ordersRange, setOrdersRange] = useState("7d");
  const [ordersStats, setOrdersStats] = useState({
    totalOrders: 0,
    goodsSum: 0,
    deliverySum: 0,
    totalSum: 0,
    deliveryOrders: 0,
    pickupOrders: 0,
    series: [],
  });
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [productsData, categoriesData] = await Promise.all([
          AdminProductService.getAllProducts(),
          AdminCategoryService.getAllCategories(),
        ]);

        const allProducts = productsData.flatMap(
          (category) => category.products
        );
        const stopListProducts = allProducts.filter(
          (p) => p.is_stop_list
        ).length;

        setStats({
          totalProducts: allProducts.length,
          stopListProducts: stopListProducts,
          totalCategories: categoriesData.length,
        });
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchOrdersStats = async () => {
      try {
        setOrdersLoading(true);
        const res = await AdminOrdersService.getOrderStats(ordersRange);
        if (res?.success) {
          setOrdersStats({
            ...res.data.summary,
            series: res.data.series || [],
          });
        }
      } catch (e) {
        console.error("Failed to fetch orders stats:", e);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrdersStats();
  }, [ordersRange]);

  return (
    <div>
      <h1 style={{ color: "rgba(0, 0, 0, 0.88)" }}>Дашборд</h1>
      <Row gutter={16}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Всего товаров"
              value={stats.totalProducts}
              prefix={<ShoppingOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="В стоп-листе"
              value={stats.stopListProducts}
              prefix={<StopOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Всего категорий"
              value={stats.totalCategories}
              prefix={<AppstoreOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ height: 16 }} />

      <Row gutter={16}>
        <Col span={24}>
          <Card
            title="Заказы"
            extra={
              <Segmented
                value={ordersRange}
                onChange={setOrdersRange}
                options={[
                  { label: "1 день", value: "1d" },
                  { label: "7 дней", value: "7d" },
                  { label: "30 дней", value: "30d" },
                  { label: "Всё время", value: "all" },
                ]}
              />
            }
          >
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Всего заказов"
                    value={ordersStats.totalOrders}
                    prefix={<ShoppingCartOutlined />}
                    loading={ordersLoading}
                  />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Доставка"
                    value={ordersStats.deliveryOrders}
                    prefix={<CarOutlined />}
                    loading={ordersLoading}
                  />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Самовывоз"
                    value={ordersStats.pickupOrders}
                    prefix={<ShopOutlined />}
                    loading={ordersLoading}
                  />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Сумма товаров"
                    value={formatRub(ordersStats.goodsSum)}
                    loading={ordersLoading}
                  />
                </Card>
              </Col>
            </Row>

            <div style={{ height: 8 }} />

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Card bordered={false}>
                  <Statistic
                    title="Сумма доставки"
                    value={formatRub(ordersStats.deliverySum)}
                    loading={ordersLoading}
                  />
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card bordered={false}>
                  <Statistic
                    title="Итого"
                    value={formatRub(ordersStats.totalSum)}
                    loading={ordersLoading}
                  />
                </Card>
              </Col>
            </Row>

            <div style={{ height: 12 }} />

            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              <div style={{ fontWeight: 700 }}>График заказов по дням</div>
              <OrdersChart series={ordersStats.series} />
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
